import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { draftAPI, dossiersAPI } from '../services/api';

// =============================================================================
// CONSTANTES & HELPERS
// =============================================================================

const CANEVAS = {
  changement_dirigeant: "Changement de gérant. Nouveau dirigeant : [nom complet]. Date d'effet : [date]. Le client souhaite être appelé avant tout dépôt.",
  transfert_siege: "Transfert de siège social. Nouvelle adresse : [adresse complète]. Date d'effet : [date]. Type d'occupation : [bail / domiciliation / propriété].",
  augmentation_capital: "Augmentation de capital de [montant ancien] à [montant nouveau] €. Mode : [numéraire / apport en nature / incorporation de réserves].",
  cession: "Cession de [nombre] parts/actions. Cédant : [nom]. Cessionnaire : [nom]. Prix : [montant] €. Agrément des associés : [obtenu / à obtenir / non requis].",
  denomination: "Changement de dénomination sociale. Ancienne dénomination : [ancien nom]. Nouvelle dénomination : [nouveau nom]. Date d'effet : [date].",
};

const TYPE_LABELS = {
  modification: 'Modification statutaire',
  creation_sas: 'Création de SAS',
  creation_sarl: 'Création de SARL',
  creation_sasu: 'Création de SASU',
  creation_eurl: 'Création de EURL',
  creation_sci: 'Création de SCI',
  dissolution: 'Dissolution',
  radiation: 'Radiation',
  depot_comptes: 'Dépôt des comptes',
  transfert_siege: 'Transfert de siège',
};

const SUB_TYPE_LABELS = {
  denomination: 'Changement de dénomination',
  dirigeant: 'Changement de dirigeant',
  siege: 'Transfert de siège',
  objet: "Changement d'objet social",
  capital_aug: 'Augmentation de capital',
  capital_red: 'Réduction de capital',
  cession: 'Cession de parts ou actions',
  forme: 'Changement de forme juridique',
  nomination: 'Nomination de dirigeant',
  exercice: 'Changement d\'exercice social',
};

// 4 types principaux de formalité (catégorisation haut-niveau pour l'UI)
const MAIN_TYPES = [
  { id: 'creation', label: 'Création de société', icon: '✦' },
  { id: 'modification', label: 'Modification statutaire', icon: '✎' },
  { id: 'fermeture', label: 'Fermeture / Dissolution', icon: '✕' },
  { id: 'depot_acte', label: 'Dépôt d\'acte', icon: '📎' },
];

// Sous-types modification (pour multi-select)
const SUB_TYPES_LIST = [
  'denomination', 'dirigeant', 'siege', 'objet',
  'capital_aug', 'capital_red', 'cession', 'forme',
  'nomination', 'exercice',
];

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 Mo

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  // Layout
  page: {
    minHeight: '100vh',
    background: '#fafaf7',
    fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
    color: '#1f1612',
    fontSize: 14,
    lineHeight: 1.55,
  },
  topbar: {
    padding: '14px 28px',
    background: 'rgba(250,250,247,0.85)',
    backdropFilter: 'saturate(180%) blur(20px)',
    borderBottom: '1px solid #ece9e2',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    position: 'sticky', top: 0, zIndex: 30,
  },
  brand: {
    fontFamily: "'Syne', serif",
    fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em',
  },
  backBtn: {
    background: 'transparent', border: 'none', cursor: 'pointer',
    color: '#555550', fontSize: 13, padding: '6px 12px',
    borderRadius: 6, fontFamily: 'inherit',
  },
  pill: {
    display: 'flex', alignItems: 'center', gap: 8,
    background: '#fff', border: '1px solid #ece9e2',
    padding: '5px 12px 5px 8px', borderRadius: 99,
    fontSize: 12, color: '#555550',
  },
  dot: {
    width: 7, height: 7, background: '#16653a', borderRadius: '50%',
    boxShadow: '0 0 0 3px rgba(22,101,58,0.12)',
  },

  // Hero (compact, même typo que le reste de Lexback)
  pane: { padding: '20px 28px 40px', minHeight: 'calc(100vh - 60px)' },
  inner: { maxWidth: 1200, margin: '0 auto' },
  hero: { textAlign: 'center', marginBottom: 18 },
  heroTitle: {
    fontFamily: "'Syne', serif",
    fontSize: 26, lineHeight: 1.15,
    fontWeight: 600, margin: '0 0 4px', letterSpacing: '-0.01em',
    color: '#1f1612',
  },
  heroEm: { fontStyle: 'normal', color: '#d9a847', fontWeight: 600 },
  heroDesc: { color: '#6b5e54', fontSize: 13.5, maxWidth: 520, margin: '0 auto' },

  // 2-column layout (left = infos, right = files)
  twoColumns: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 20,
    alignItems: 'start',
  },
  columnLeft: { display: 'flex', flexDirection: 'column' },
  columnRight: { display: 'flex', flexDirection: 'column', position: 'sticky', top: 80 },

  // Block
  block: {
    background: '#fff', border: '1px solid #ece9e2',
    borderRadius: 12, padding: '14px 16px', marginBottom: 12,
    boxShadow: '0 1px 2px rgba(20,18,14,0.04)',
  },
  blockTitle: {
    fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: '0.06em', color: '#555550', marginBottom: 10,
  },

  // Tabs
  tabsRow: {
    display: 'inline-flex', background: '#f5f4ef', border: '1px solid #ece9e2',
    borderRadius: 8, padding: 3, marginBottom: 12,
  },
  tab: {
    background: 'transparent', border: 'none', padding: '7px 14px',
    fontSize: 12.5, color: '#555550', cursor: 'pointer',
    fontFamily: 'inherit', borderRadius: 6,
    display: 'inline-flex', alignItems: 'center', gap: 6,
  },
  tabActive: {
    background: '#fff', color: '#1a1a1a', fontWeight: 500,
    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
  },

  // SIREN input
  sirenWrap: { position: 'relative' },
  sirenInput: {
    width: '100%', padding: '11px 14px',
    border: '1px solid #ddd9d0', background: '#fff',
    fontSize: 15, fontFamily: 'inherit', outline: 'none',
    borderRadius: 8, fontVariantNumeric: 'tabular-nums',
    letterSpacing: '0.5px', boxSizing: 'border-box',
  },
  sirenValid: { borderColor: '#16653a', background: '#fafdfb', paddingRight: 38 },

  // Création info
  creationInfo: {
    background: '#eff6ff', border: '1px solid #bfdbfe',
    borderRadius: 8, padding: '11px 14px',
    fontSize: 13, color: '#1e3a8a', lineHeight: 1.5,
    display: 'flex', gap: 10, alignItems: 'flex-start',
  },

  // Société card
  societeCard: {
    background: 'linear-gradient(180deg, #fafdfb 0%, #ffffff 100%)',
    border: '1px solid #c8e2d3', borderRadius: 10,
    padding: '14px 16px', marginTop: 12,
  },
  societeName: { fontSize: 15, fontWeight: 600, color: '#1a1a1a' },
  societeMeta: {
    fontSize: 12.5, color: '#555550', marginTop: 4,
    display: 'flex', flexWrap: 'wrap', gap: 14,
  },
  pillGreen: {
    display: 'inline-block', padding: '2.5px 8px', borderRadius: 4,
    fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase',
    background: '#e3f1e9', color: '#16653a',
  },
  pillBlue: {
    display: 'inline-block', padding: '2.5px 8px', borderRadius: 4,
    fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase',
    background: '#eff6ff', color: '#2563eb',
  },
  societeActions: {
    display: 'flex', flexWrap: 'wrap', gap: 6,
    marginTop: 12, paddingTop: 12, borderTop: '1px solid #d4e9dc',
  },
  societeActionBtn: {
    background: '#fff', border: '1px solid #ece9e2',
    padding: '6px 12px', borderRadius: 6, fontSize: 12,
    color: '#555550', cursor: 'pointer', fontFamily: 'inherit',
    display: 'inline-flex', alignItems: 'center', gap: 5,
  },

  // Drop zone
  dropZone: {
    background: '#fff', border: '2px dashed #ddd9d0',
    borderRadius: 12, padding: '28px 20px',
    textAlign: 'center', cursor: 'pointer',
    transition: 'all 0.2s', marginBottom: 12,
  },
  dropZoneDrag: {
    borderColor: '#c78c2a', background: '#fcf4dd', borderStyle: 'solid',
  },
  dropIconWrap: {
    width: 48, height: 48, margin: '0 auto 12px',
    background: '#f7efd9', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#c78c2a',
  },
  dropTitle: { fontSize: 15, fontWeight: 500, color: '#1a1a1a', marginBottom: 3 },
  dropSubtitle: { fontSize: 12.5, color: '#555550' },
  dropFormats: { marginTop: 10, fontSize: 11.5, color: '#8e8b85' },
  dropBtn: {
    marginTop: 12, padding: '8px 18px',
    background: '#1f1612', color: '#fff', border: 'none',
    borderRadius: 7, fontSize: 12.5, fontWeight: 500,
    cursor: 'pointer', fontFamily: 'inherit',
  },

  // Files
  filesAttached: {
    background: '#fff', border: '1px solid #ece9e2',
    borderRadius: 12, padding: '14px 16px', marginBottom: 14,
    boxShadow: '0 1px 2px rgba(20,18,14,0.04)',
  },
  filesHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 10,
  },
  filesTitle: {
    fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: '0.06em', color: '#555550',
  },
  filesAdd: {
    fontSize: 12, color: '#c78c2a', cursor: 'pointer',
    background: 'transparent', border: 'none', padding: 0, fontFamily: 'inherit',
  },
  filesList: { display: 'flex', flexDirection: 'column', gap: 5 },
  fileRow: {
    display: 'flex', alignItems: 'center', gap: 9,
    padding: '7px 11px', background: '#f5f4ef',
    border: '1px solid #ece9e2', borderRadius: 7,
  },
  fileIc: {
    width: 26, height: 26, borderRadius: 4,
    color: '#fff', fontSize: 9, fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  fileName: { fontWeight: 500, fontSize: 12.5, color: '#1a1a1a' },
  fileSize: { fontSize: 11, color: '#8e8b85', marginTop: 1 },
  fileRm: {
    background: 'transparent', border: 'none', color: '#8e8b85',
    fontSize: 16, cursor: 'pointer', padding: '2px 8px',
  },

  // Comment
  commentSection: {
    background: '#fff', border: '1px solid #ece9e2',
    borderRadius: 12, padding: '16px 18px', marginBottom: 14,
    boxShadow: '0 1px 2px rgba(20,18,14,0.04)',
  },
  commentLabel: {
    fontSize: 11, color: '#555550', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.06em',
  },
  commentTextarea: {
    width: '100%', padding: '11px 13px',
    border: '1px solid #ddd9d0', background: '#fff',
    fontSize: 14, fontFamily: 'inherit', outline: 'none',
    borderRadius: 8, resize: 'vertical', minHeight: 80,
    lineHeight: 1.5, marginTop: 8, boxSizing: 'border-box',
  },
  canevasHeader: { fontSize: 11, color: '#8e8b85', marginTop: 10, marginBottom: 4 },
  canevasList: { display: 'flex', flexWrap: 'wrap', gap: 5 },
  canevasChip: {
    background: '#f5f4ef', border: '1px solid #ece9e2',
    padding: '6px 11px', borderRadius: 99,
    fontSize: 11.5, color: '#555550', cursor: 'pointer',
    fontFamily: 'inherit',
  },

  // Type chips (4 main types) — côte à côte sur une ligne
  typeChips: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 10,
  },
  typeChip: {
    background: '#fff', border: '1px solid #ddd9d0',
    padding: '14px 12px', borderRadius: 10,
    cursor: 'pointer', fontFamily: 'inherit',
    textAlign: 'center', transition: 'all 0.15s',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
    width: '100%',
  },
  typeChipActive: {
    borderColor: '#1f1612', background: '#1f1612', color: '#fff',
    boxShadow: '0 2px 6px rgba(31,22,18,0.15)',
  },
  typeChipIcon: {
    fontSize: 16, lineHeight: 1, flexShrink: 0,
    fontFamily: "'Syne', serif",
    color: '#d9a847',
  },
  typeChipLabel: { fontSize: 12.5, fontWeight: 500 },

  // Sous-types chips (multi-select)
  subTypesGrid: {
    display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 12,
  },
  subChip: {
    background: '#fff', border: '1px solid #ddd9d0',
    padding: '7px 13px', borderRadius: 99,
    fontSize: 12.5, color: '#555550', cursor: 'pointer',
    fontFamily: 'inherit', transition: 'all 0.15s',
  },
  subChipActive: {
    background: '#1a1a1a', borderColor: '#1a1a1a', color: '#fff',
    fontWeight: 500,
  },

  // Direct submit button
  btnDirect: {
    padding: '12px 20px', background: '#fff', color: '#1a1a1a',
    border: '1.5px solid #1a1a1a', borderRadius: 10,
    fontSize: 14, fontWeight: 500, cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'inline-flex', alignItems: 'center', gap: 8,
    transition: 'all 0.15s',
  },
  btnDirectDisabled: {
    borderColor: '#ddd9d0', color: '#8e8b85', cursor: 'not-allowed',
  },
  buttonsRow: {
    display: 'flex', gap: 10, justifyContent: 'space-between',
    alignItems: 'center', flexWrap: 'wrap', marginBottom: 14,
  },
  buttonsRowInfo: {
    flex: 1, minWidth: 200, fontSize: 12.5, color: '#8e8b85',
  },
  buttonsGroup: {
    display: 'flex', gap: 8, flexWrap: 'wrap',
  },

  // Submit row
  submitRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: 12, marginBottom: 14, flexWrap: 'wrap',
  },
  submitInfo: { flex: 1, fontSize: 12.5, color: '#8e8b85' },
  btnAnalyze: {
    padding: '12px 22px', background: '#1a1a1a', color: '#fff',
    border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 500,
    cursor: 'pointer', fontFamily: 'inherit',
    display: 'inline-flex', alignItems: 'center', gap: 8,
  },
  btnAnalyzeDisabled: { background: '#ddd9d0', cursor: 'not-allowed' },

  // Privacy
  privacy: {
    background: '#e3f1e9', border: '1px solid #c8e2d3',
    borderRadius: 10, padding: '10px 14px',
    display: 'flex', gap: 10, alignItems: 'flex-start',
    fontSize: 12.5, color: '#1a4a30', lineHeight: 1.5,
  },
  privacyIc: {
    flexShrink: 0, width: 18, height: 18,
    background: '#16653a', color: '#fff', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },

  // Overlay
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(20,18,14,0.45)',
    backdropFilter: 'blur(8px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 100,
  },
  overlayPanel: {
    background: '#fff', borderRadius: 16,
    padding: '36px 44px', maxWidth: 480, width: '90%',
    textAlign: 'center', boxShadow: '0 12px 40px rgba(20,18,14,0.12)',
  },
  overlayTitle: {
    fontFamily: "'Syne', serif",
    fontSize: 22, margin: '14px 0 6px',
  },
  overlayStep: { fontSize: 14, color: '#555550', marginBottom: 20, minHeight: 22 },
  progressTrack: {
    height: 4, background: '#ebe9e2', borderRadius: 99, overflow: 'hidden',
  },
  progressBar: {
    height: '100%', background: 'linear-gradient(90deg, #c78c2a, #1a1a1a)',
    transition: 'width 0.4s ease-out', borderRadius: 99,
  },
  spinnerBig: {
    width: 56, height: 56, position: 'relative', display: 'inline-block',
  },

  // Modal
  modalBg: {
    position: 'fixed', inset: 0, background: 'rgba(20,18,14,0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 90, padding: 24,
  },
  modalCard: {
    background: '#fff', borderRadius: 14, padding: '24px 28px',
    maxWidth: 460, width: '100%',
    boxShadow: '0 12px 40px rgba(20,18,14,0.12)',
  },
  modalTitle: {
    fontFamily: "'Syne', serif",
    fontSize: 22, margin: '0 0 4px', fontWeight: 400,
  },
  pillWarn: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    background: '#fef3c7', color: '#b45309',
    padding: '3px 10px', borderRadius: 99,
    fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: '0.04em', marginBottom: 10,
  },
  modalOption: {
    background: '#fff', border: '1px solid #ddd9d0',
    borderRadius: 7, padding: '9px 12px', cursor: 'pointer',
    fontSize: 12.5, color: '#555550',
    fontFamily: 'inherit', textAlign: 'left', width: '100%',
    marginBottom: 5,
  },
  modalOptionSelected: {
    background: '#1a1a1a', color: '#fff',
    borderColor: '#1a1a1a', fontWeight: 500,
  },
  modalSkip: {
    background: 'transparent', border: 'none', color: '#8e8b85',
    fontSize: 11.5, cursor: 'pointer', padding: 0,
    textDecoration: 'underline', fontFamily: 'inherit',
  },

  // Confirm
  confirmPill: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: '#e3f1e9', color: '#16653a',
    padding: '5px 14px', borderRadius: 99,
    fontSize: 12, fontWeight: 600, marginBottom: 12,
    textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  confirmCard: {
    background: '#fff', border: '1px solid #ece9e2',
    borderRadius: 12, padding: '20px 22px', marginBottom: 12,
    boxShadow: '0 1px 2px rgba(20,18,14,0.04)',
  },
  confirmCardHead: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid #ece9e2',
  },
  confirmCardTitle: {
    fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: '0.06em', color: '#555550',
  },
  confirmEdit: {
    fontSize: 12, color: '#c78c2a', cursor: 'pointer',
    background: 'transparent', border: 'none', padding: 0,
    fontFamily: 'inherit',
  },
  confirmGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 22px',
  },
  unknownVal: { color: '#b45309', fontStyle: 'italic', fontWeight: 500 },

  // Pieces classified
  docClassified: {
    display: 'flex', alignItems: 'center', gap: 11,
    padding: '10px 13px', background: '#f5f4ef',
    border: '1px solid #ece9e2', borderRadius: 8,
    marginBottom: 7,
  },
  docTag: {
    background: '#f7efd9', color: '#c78c2a',
    padding: '1px 6px', borderRadius: 3,
    fontSize: 10.5, fontWeight: 600, letterSpacing: '0.02em',
    marginLeft: 3, display: 'inline-block',
  },

  // Missing warn
  missingWarn: {
    marginTop: 11, background: '#fef3c7', border: '1px solid #fde68a',
    borderRadius: 8, padding: '11px 14px',
    display: 'flex', gap: 10, alignItems: 'flex-start',
  },

  // Provision
  provisionRow: {
    background: '#f5f4ef', border: '1px solid #ece9e2',
    borderRadius: 8, padding: '12px 14px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  provisionAmount: {
    fontSize: 22, fontWeight: 600,
    fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em',
  },
  provisionIndic: {
    fontSize: 11, fontWeight: 500, color: '#8e8b85',
    marginLeft: 6, verticalAlign: 'middle',
  },
  walletWarn: {
    fontSize: 11.5, color: '#b45309', marginTop: 6,
    display: 'flex', alignItems: 'center', gap: 5,
  },

  // Confirm actions
  confirmActions: {
    display: 'flex', gap: 10, justifyContent: 'flex-end',
    marginTop: 18, flexWrap: 'wrap',
  },
  btn: {
    padding: '11px 22px', borderRadius: 10, cursor: 'pointer',
    fontSize: 13.5, fontWeight: 500, fontFamily: 'inherit',
    border: '1px solid',
  },
  btnSecondary: {
    background: '#fff', borderColor: '#ece9e2', color: '#555550',
  },
  btnPrimary: {
    background: '#1a1a1a', borderColor: '#1a1a1a', color: '#fff',
  },

  // Sent
  sentIcon: {
    width: 72, height: 72, margin: '0 auto 18px',
    background: '#e3f1e9', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#16653a',
  },
};

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================

export default function NouveauDossier() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Phase actuelle : 'form' | 'analyzing' | 'modal' | 'confirm' | 'sent' | 'submitting'
  const [phase, setPhase] = useState('form');

  // État du brouillon
  const [draftId, setDraftId] = useState(null);
  const [tab, setTab] = useState('siren'); // 'siren' | 'creation'
  const [siren, setSiren] = useState('');
  const [societe, setSociete] = useState(null);     // {nom, forme, capital, adresse, statut}
  const [sirenLoading, setSirenLoading] = useState(false);
  const [files, setFiles] = useState([]);            // [{id, name, size, mime, detected_label, detected_by_ai}]
  const [comment, setComment] = useState('');
  const [dragOver, setDragOver] = useState(false);
  // Date du dossier — par défaut aujourd'hui, modifiable (au format YYYY-MM-DD)
  const [dateDossier, setDateDossier] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  });

  // Type de formalité et sous-types (sélection manuelle, optionnelle)
  const [mainType, setMainType] = useState(null);          // 'creation' | 'modification' | 'fermeture' | 'depot_acte'
  const [subTypesSelected, setSubTypesSelected] = useState([]);   // ['dirigeant', 'siege', ...]

  // Résultat de l'analyse IA
  const [analyzeResult, setAnalyzeResult] = useState(null); // {type_formalite, sous_types, documents, missing_required, provision_indicative}

  // Progress de l'analyse
  const [analyzeStep, setAnalyzeStep] = useState('');
  const [analyzeProgress, setAnalyzeProgress] = useState(0);

  // Modal questions
  const [modalAnswer, setModalAnswer] = useState(null);

  // Erreurs
  const [error, setError] = useState('');

  // Réf finale pour l'écran "Envoyé"
  const [finalReference, setFinalReference] = useState('');

  // ─── Cleanup à l'unmount : annule le brouillon si pas soumis
  // (on évite la purge automatique trop tôt — c'est le cron qui purgera après 7j)
  // Si l'utilisateur quitte sans envoyer, le brouillon reste dans la DB
  // jusqu'au cron de purge.

  // ─── SIREN debounce ──────────────────────────────────────────────────────
  useEffect(() => {
    if (tab !== 'siren') return;
    const clean = siren.replace(/\s/g, '');
    if (clean.length !== 9 || !/^\d+$/.test(clean)) {
      setSociete(null);
      return;
    }
    setSirenLoading(true);
    const tid = setTimeout(async () => {
      try {
        // Appel Pappers via backend (route déjà existante)
        const res = await dossiersAPI.pappersLookup(clean);
        const data = res.data;
        if (data) {
          // Format adresse complète : "adresse, code_postal ville"
          const siege = data.siege || {};
          let adresse = siege.adresse || '';
          if (siege.code_postal || siege.ville) {
            adresse += (adresse ? ', ' : '') + [siege.code_postal, siege.ville].filter(Boolean).join(' ');
          }
          setSociete({
            nom: data.nom || 'Société identifiée',
            forme: data.forme_juridique || '',
            capital: data.capital ? `${Number(data.capital).toLocaleString('fr-FR')} €` : '',
            adresse: adresse,
            statut: data.statut || 'Actif',
            data,
          });
          // Créer (ou rafraîchir) le brouillon en DB dès qu'on a un SIREN valide
          await ensureDraft({ siren: clean, is_creation: false });
        }
      } catch (err) {
        console.error('Pappers lookup failed:', err);
        setError('Société introuvable pour ce SIREN');
        setSociete(null);
      } finally {
        setSirenLoading(false);
      }
    }, 600);
    return () => clearTimeout(tid);
  }, [siren, tab]);

  // ─── Brouillon backend : create / update ─────────────────────────────────
  const ensureDraft = useCallback(async ({ siren, is_creation } = {}) => {
    if (draftId) return draftId; // déjà créé
    try {
      const payload = is_creation ? { is_creation: true } : { siren, is_creation: false };
      if (comment) payload.commentaire = comment;
      if (mainType) payload.type_formalite = mainType;
      if (subTypesSelected.length > 0) payload.sous_types = subTypesSelected;
      const res = await draftAPI.create(payload);
      setDraftId(res.data.id);
      return res.data.id;
    } catch (err) {
      console.error('Draft create failed:', err);
      setError("Impossible de créer le brouillon. Vérifie ta connexion.");
      return null;
    }
  }, [draftId, comment, mainType, subTypesSelected]);

  // ─── Sync type / sous-types vers le brouillon (PATCH /{id}) ──────────────
  useEffect(() => {
    if (!draftId) return;
    const tid = setTimeout(() => {
      const payload = {};
      if (mainType !== null) payload.type_formalite = mainType;
      if (subTypesSelected) payload.sous_types = subTypesSelected;
      api.patch(`/api/dossiers/draft/${draftId}`, payload).catch(() => {});
    }, 500);
    return () => clearTimeout(tid);
  }, [mainType, subTypesSelected, draftId]);

  // Switch SIREN <-> Création
  const switchTab = async (t) => {
    if (t === tab) return;
    setTab(t);
    setError('');
    if (t === 'creation') {
      setSiren('');
      setSociete(null);
      // Créer brouillon en mode création tout de suite
      if (!draftId) await ensureDraft({ is_creation: true });
    }
  };

  // ─── File handling ───────────────────────────────────────────────────────
  const handleFiles = async (selectedFiles) => {
    setError('');
    const fileList = Array.from(selectedFiles);
    if (fileList.length === 0) return;

    // S'assurer qu'on a un brouillon
    let did = draftId;
    if (!did) {
      did = await ensureDraft(
        tab === 'creation'
          ? { is_creation: true }
          : { siren: siren.replace(/\s/g, ''), is_creation: false }
      );
      if (!did) return;
    }

    // Upload séquentiel pour ne pas saturer le backend
    for (const file of fileList) {
      if (file.size > MAX_FILE_SIZE) {
        setError(`Fichier ${file.name} > 25 Mo, ignoré.`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await draftAPI.upload(did, fd);
        setFiles((prev) => [...prev, {
          id: res.data.id,
          name: res.data.nom_original,
          size: res.data.taille_kb,
          mime: res.data.mime_type,
          detected_label: res.data.detected_label,
          detected_by_ai: res.data.detected_by_ai,
        }]);
      } catch (err) {
        console.error('Upload failed:', err);
        setError(`Échec de l'upload : ${file.name}`);
      }
    }
  };

  const onFilePicked = (e) => {
    handleFiles(e.target.files);
    e.target.value = ''; // permet de re-uploader le même fichier
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files) handleFiles(e.dataTransfer.files);
  };

  const removeFile = async (attId) => {
    if (!draftId) return;
    try {
      await draftAPI.removeAttachment(draftId, attId);
      setFiles((prev) => prev.filter((f) => f.id !== attId));
    } catch (err) {
      console.error('Remove attachment failed:', err);
    }
  };

  // ─── Canevas ─────────────────────────────────────────────────────────────
  const useCanevas = (key) => {
    if (comment && !window.confirm("Remplacer le commentaire actuel par ce canevas ?")) return;
    setComment(CANEVAS[key] || '');
  };

  // Sync commentaire vers brouillon (debounce)
  useEffect(() => {
    if (!draftId) return;
    const tid = setTimeout(() => {
      draftAPI.updateComment(draftId, { commentaire: comment }).catch(() => {});
    }, 600);
    return () => clearTimeout(tid);
  }, [comment, draftId]);

  // ─── Analyze ────────────────────────────────────────────────────────────
  const canAnalyze = (tab === 'creation' || societe) && files.length > 0 && draftId;
  // Envoi direct : société identifiée + au moins UN signal (fichier OU commentaire OU type)
  const canSubmitDirect = (tab === 'creation' || societe) && draftId && (
    files.length > 0 || comment.trim().length > 0 || mainType !== null
  );

  const startAnalyze = async () => {
    if (!canAnalyze) return;
    setPhase('analyzing');
    setError('');

    // Animation de progress côté front pendant que l'IA tourne réellement
    const stepLabels = [
      "Lecture des documents…",
      "Détection du type de formalité…",
      "Classement des pièces…",
      "Vérification des pièces requises…",
    ];
    let pi = 0;
    setAnalyzeStep(stepLabels[0]);
    setAnalyzeProgress(20);
    const interval = setInterval(() => {
      pi++;
      if (pi < stepLabels.length) {
        setAnalyzeStep(stepLabels[pi]);
        setAnalyzeProgress(((pi + 1) / stepLabels.length) * 90);
      }
    }, 1500);

    try {
      const res = await draftAPI.analyze(draftId);
      clearInterval(interval);
      setAnalyzeProgress(100);
      setAnalyzeResult(res.data);

      // Mettre à jour les labels des fichiers
      const updated = files.map((f) => {
        const found = res.data.documents?.find((d) => d.attachment_id === f.id);
        return found ? { ...f, detected_label: found.label, detected_by_ai: true } : f;
      });
      setFiles(updated);

      // Pré-remplir les chips type + sous-types avec ce que l'IA a détecté,
      // SAUF si l'utilisateur avait déjà fait un choix manuel (on ne l'écrase pas).
      if (!mainType && res.data.type_formalite) {
        const aiMainType = mapAITypeToMain(res.data.type_formalite);
        if (aiMainType) setMainType(aiMainType);
      }
      if (subTypesSelected.length === 0 && res.data.sous_types?.length > 0) {
        setSubTypesSelected(res.data.sous_types);
      }

      // Si pas de type détecté ET pas en création → modale de questions
      setTimeout(() => {
        if (!res.data.type_formalite && !mainType && tab === 'siren') {
          setPhase('modal');
        } else {
          setPhase('confirm');
        }
      }, 350);
    } catch (err) {
      clearInterval(interval);
      console.error('Analyze failed:', err);
      setError("L'analyse a échoué. Vous pouvez réessayer ou continuer sans.");
      setPhase('form');
    }
  };

  // ─── Modale (questions de précisions) ────────────────────────────────────
  const closeModal = (skipped) => {
    if (skipped) {
      setAnalyzeResult((prev) => ({ ...prev, type_formalite: null }));
    } else if (modalAnswer) {
      // Si l'utilisateur a choisi un sous-type modification, on définit le type
      const isModSubType = ['dirigeant', 'siege', 'capital_aug', 'cession', 'denomination'].includes(modalAnswer);
      if (isModSubType) {
        setAnalyzeResult((prev) => ({
          ...prev,
          type_formalite: 'modification',
          sous_types: [modalAnswer],
        }));
      } else if (modalAnswer === 'creation_society') {
        setAnalyzeResult((prev) => ({ ...prev, type_formalite: 'creation_sas' }));
      } else {
        setAnalyzeResult((prev) => ({
          ...prev,
          type_formalite: 'modification',
          sous_types: ['autre'],
        }));
      }
    }
    setModalAnswer(null);
    setPhase('confirm');
  };

  // ─── Submit ─────────────────────────────────────────────────────────────
  // Submit après analyse IA : on inclut les choix utilisateur ET les sous-types détectés par l'IA
  const handleSubmit = async () => {
    if (!draftId) return;
    setPhase('submitting');
    setError('');
    try {
      // Sous-types finaux = ceux choisis manuellement par l'utilisateur
      // (l'IA les a déjà fusionnés dans le brouillon via le PATCH automatique)
      const payload = {
        commentaire_final: comment,
        skip_missing_pieces: true,
        type_formalite: mainType || (analyzeResult?.type_formalite ? mapAITypeToMain(analyzeResult.type_formalite) : null),
        sous_types: subTypesSelected.length > 0 ? subTypesSelected : (analyzeResult?.sous_types || null),
        direct_submit: false,
        date_dossier: dateDossier,
      };
      const res = await draftAPI.submit(draftId, payload);
      setFinalReference(res.data.reference);
      setPhase('sent');
    } catch (err) {
      console.error('Submit failed:', err);
      setError("L'envoi a échoué. Vous pouvez réessayer.");
      setPhase('confirm');
    }
  };

  // Envoi DIRECT au formaliste sans passer par l'analyse IA
  const handleDirectSubmit = async () => {
    // Validations minimales
    if (!draftId) {
      // Force la création du brouillon si pas encore fait
      const did = await ensureDraft(
        tab === 'creation' ? { is_creation: true } : { siren: siren.replace(/\s/g, ''), is_creation: false }
      );
      if (!did) return;
    }
    if (files.length === 0 && !comment) {
      if (!window.confirm("Vous n'avez ni pièce ni commentaire. Envoyer quand même au formaliste ?")) return;
    }
    setPhase('submitting');
    setError('');
    try {
      const payload = {
        commentaire_final: comment,
        skip_missing_pieces: true,
        type_formalite: mainType,
        sous_types: subTypesSelected.length > 0 ? subTypesSelected : null,
        direct_submit: true,
        date_dossier: dateDossier,
      };
      const res = await draftAPI.submit(draftId, payload);
      setFinalReference(res.data.reference);
      setPhase('sent');
    } catch (err) {
      console.error('Direct submit failed:', err);
      setError("L'envoi a échoué. Vous pouvez réessayer.");
      setPhase('form');
    }
  };

  // Helper : convertit un type IA (modification, creation_sas...) en MAIN_TYPE
  const mapAITypeToMain = (aiType) => {
    if (!aiType) return null;
    if (aiType.startsWith('creation')) return 'creation';
    if (aiType === 'modification') return 'modification';
    if (aiType === 'dissolution' || aiType === 'radiation') return 'fermeture';
    if (aiType === 'depot_comptes') return 'depot_acte';
    return null;
  };

  const resetAll = () => {
    setPhase('form');
    setDraftId(null);
    setTab('siren');
    setSiren('');
    setSociete(null);
    setFiles([]);
    setComment('');
    setMainType(null);
    setSubTypesSelected([]);
    setAnalyzeResult(null);
    setFinalReference('');
    setError('');
  };

  // ─── Helpers d'affichage ─────────────────────────────────────────────────
  const getFees = () => {
    if (!analyzeResult) return null;
    return analyzeResult.provision_indicative;
  };

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <div style={styles.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Syne:wght@500;600;700&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* === PHASE FORM === */}
      {(phase === 'form' || phase === 'submitting') && (
        <div style={styles.pane}>
          <div style={styles.inner}>

            <div style={styles.hero}>
              <h1 style={styles.heroTitle}>
                Déposez votre dossier, <span style={styles.heroEm}>nous l'organisons</span>
              </h1>
              <p style={styles.heroDesc}>
                Indiquez la société, déposez les pièces, l'IA fait le reste.
              </p>
            </div>

            {/* Erreur */}
            {error && (
              <div style={{ ...styles.privacy, background: '#fbe9e7', borderColor: '#fda3a0', color: '#b3261e', marginBottom: 14 }}>
                <div style={{ ...styles.privacyIc, background: '#b3261e' }}>!</div>
                <div>{error}</div>
              </div>
            )}

            {/* === 2 COLONNES : gauche (infos) | droite (fichiers) === */}
            <div style={styles.twoColumns}>

            {/* ┌─ COLONNE GAUCHE : Société + Type + Commentaire ─┐ */}
            <div style={styles.columnLeft}>

            {/* DATE DU DOSSIER (modifiable, par défaut aujourd'hui) */}
            <div style={styles.block}>
              <div style={styles.blockTitle}>
                Date du dossier <span style={{ color: '#8e8b85', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— par défaut aujourd'hui</span>
              </div>
              <input
                type="date"
                value={dateDossier}
                onChange={(e) => setDateDossier(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
                style={{
                  width: 'auto', padding: '10px 14px',
                  border: '1px solid #ddd9d0', background: '#fff',
                  fontSize: 14, fontFamily: 'inherit', outline: 'none',
                  borderRadius: 8, color: '#1f1612',
                  fontVariantNumeric: 'tabular-nums',
                  cursor: 'pointer',
                }}
              />
            </div>

            {/* BLOC SOCIÉTÉ */}
            <div style={styles.block}>
              <div style={styles.blockTitle}>Société concernée <span style={{ color: '#b3261e' }}>*</span></div>

              <div style={styles.tabsRow}>
                <button
                  style={{ ...styles.tab, ...(tab === 'siren' ? styles.tabActive : {}) }}
                  onClick={() => switchTab('siren')}
                >
                  Société existante
                </button>
                <button
                  style={{ ...styles.tab, ...(tab === 'creation' ? styles.tabActive : {}) }}
                  onClick={() => switchTab('creation')}
                >
                  Création de société
                </button>
              </div>

              {tab === 'siren' && (
                <>
                  <div style={styles.sirenWrap}>
                    <input
                      type="text"
                      style={{ ...styles.sirenInput, ...(societe ? styles.sirenValid : {}) }}
                      placeholder="SIREN (9 chiffres)"
                      maxLength={11}
                      value={siren}
                      onChange={(e) => setSiren(e.target.value)}
                    />
                    {sirenLoading && (
                      <div style={{
                        position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                        width: 16, height: 16,
                        border: '2px solid #ebe9e2', borderTopColor: '#c78c2a',
                        borderRadius: '50%', animation: 'spin 0.8s linear infinite',
                      }} />
                    )}
                    {societe && !sirenLoading && (
                      <div style={{
                        position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                        width: 18, height: 18, background: '#16653a', color: '#fff',
                        borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700,
                      }}>✓</div>
                    )}
                  </div>

                  {societe && (
                    <SocieteCard societe={societe} siren={siren} draftId={draftId} />
                  )}
                </>
              )}

              {tab === 'creation' && (
                <div style={styles.creationInfo}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="16" x2="12" y2="12"/>
                    <line x1="12" y1="8" x2="12.01" y2="8"/>
                  </svg>
                  <div>
                    Vous créez une <strong>nouvelle société</strong>. Aucun SIREN à renseigner — il sera attribué après immatriculation au greffe.
                  </div>
                </div>
              )}
            </div>

            {/* TYPE DE FORMALITÉ */}
            <div style={styles.block}>
              <div style={styles.blockTitle}>
                Type de formalité <span style={{ color: '#8e8b85', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— optionnel, mais accélère l'analyse</span>
              </div>
              <div style={styles.typeChips}>
                {MAIN_TYPES.map((t) => (
                  <button
                    key={t.id}
                    style={{ ...styles.typeChip, ...(mainType === t.id ? styles.typeChipActive : {}) }}
                    onClick={() => {
                      if (mainType === t.id) {
                        setMainType(null);
                        setSubTypesSelected([]);
                      } else {
                        setMainType(t.id);
                        // Reset sous-types si on change de type principal
                        if (t.id !== 'modification') setSubTypesSelected([]);
                      }
                    }}
                  >
                    <span style={styles.typeChipIcon}>{t.icon}</span>
                    <span style={styles.typeChipLabel}>{t.label}</span>
                  </button>
                ))}
              </div>

              {/* SOUS-TYPES (uniquement si Modification) */}
              {mainType === 'modification' && (
                <>
                  <div style={{ ...styles.blockTitle, marginTop: 16, marginBottom: 8 }}>
                    Nature de la modification <span style={{ color: '#8e8b85', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— plusieurs choix possibles</span>
                  </div>
                  <div style={styles.subTypesGrid}>
                    {SUB_TYPES_LIST.map((st) => {
                      const selected = subTypesSelected.includes(st);
                      return (
                        <button
                          key={st}
                          style={{ ...styles.subChip, ...(selected ? styles.subChipActive : {}) }}
                          onClick={() => {
                            if (selected) {
                              setSubTypesSelected(subTypesSelected.filter((x) => x !== st));
                            } else {
                              setSubTypesSelected([...subTypesSelected, st]);
                            }
                          }}
                        >
                          {SUB_TYPE_LABELS[st] || st}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* └─ fin colonne gauche ─┘ */}
            </div>

            {/* ┌─ COLONNE DROITE : Drop zone + Files ─┐ */}
            <div style={styles.columnRight}>

            {/* DROP ZONE */}
            <div
              style={{ ...styles.dropZone, ...(dragOver ? styles.dropZoneDrag : {}) }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
            >
              <div style={styles.dropIconWrap}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
                </svg>
              </div>
              <div style={styles.dropTitle}>Glissez vos documents ici</div>
              <div style={styles.dropSubtitle}>ou cliquez pour parcourir</div>
              <button style={styles.dropBtn} onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}>
                Parcourir les fichiers
              </button>
              <div style={styles.dropFormats}>PDF, DOCX, JPG, PNG — jusqu'à 25 Mo par fichier</div>
              <input
                type="file"
                ref={fileInputRef}
                multiple
                accept=".pdf,.docx,.jpg,.jpeg,.png"
                style={{ display: 'none' }}
                onChange={onFilePicked}
              />
            </div>

            {/* Files */}
            {files.length > 0 && (
              <div style={styles.filesAttached}>
                <div style={styles.filesHeader}>
                  <div style={styles.filesTitle}>
                    Fichiers déposés <span style={{ color: '#8e8b85' }}>({files.length})</span>
                  </div>
                  <button style={styles.filesAdd} onClick={() => fileInputRef.current?.click()}>+ Ajouter</button>
                </div>
                <div style={styles.filesList}>
                  {files.map((f) => {
                    const ext = (f.name.split('.').pop() || '').toUpperCase();
                    const isImg = ['JPG', 'JPEG', 'PNG'].includes(ext);
                    return (
                      <div key={f.id} style={styles.fileRow}>
                        <div style={{ ...styles.fileIc, background: isImg ? '#16653a' : '#b3261e' }}>{ext}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={styles.fileName}>{f.name}</div>
                          <div style={styles.fileSize}>{f.size} Ko</div>
                        </div>
                        <button style={styles.fileRm} onClick={() => removeFile(f.id)}>×</button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* └─ fin colonne droite ─┘ */}
            </div>

            {/* === fin 2 colonnes === */}
            </div>

            {/* Commentaire */}
            <div style={styles.commentSection}>
              <label style={styles.commentLabel}>
                Commentaire <span style={{ color: '#8e8b85', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— optionnel</span>
              </label>
              <textarea
                style={styles.commentTextarea}
                placeholder="Décrivez en quelques mots la formalité (ex : 'Changement de gérant — urgent, le client souhaite être appelé avant dépôt')"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <div style={styles.canevasHeader}>Canevas rapides :</div>
              <div style={styles.canevasList}>
                {Object.keys(CANEVAS).map((key) => (
                  <button key={key} style={styles.canevasChip} onClick={() => useCanevas(key)}>
                    {key === 'changement_dirigeant' && 'Changement de gérant'}
                    {key === 'transfert_siege' && 'Transfert de siège'}
                    {key === 'augmentation_capital' && 'Augmentation de capital'}
                    {key === 'cession' && 'Cession de parts'}
                    {key === 'denomination' && 'Changement de dénomination'}
                  </button>
                ))}
              </div>
            </div>

            {/* Boutons d'action : Envoyer direct OU Analyser avec l'IA */}
            <div style={styles.buttonsRow}>
              <div style={styles.buttonsRowInfo}>
                {canAnalyze ? (
                  <><strong>L'IA</strong> classe automatiquement vos pièces avant envoi. Sinon, vous pouvez envoyer directement.</>
                ) : (
                  <>Renseignez la société {files.length === 0 ? 'et déposez au moins une pièce' : ''} pour activer l'envoi.</>
                )}
              </div>
              <div style={styles.buttonsGroup}>
                <button
                  style={{ ...styles.btnDirect, ...(canSubmitDirect ? {} : styles.btnDirectDisabled) }}
                  disabled={!canSubmitDirect}
                  onClick={handleDirectSubmit}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13"/>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                  Envoyer directement
                </button>
                <button
                  style={{ ...styles.btnAnalyze, ...(canAnalyze ? {} : styles.btnAnalyzeDisabled) }}
                  disabled={!canAnalyze}
                  onClick={startAnalyze}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4"/>
                  </svg>
                  Analyser avec l'IA
                </button>
              </div>
            </div>

            {/* Privacy */}
            <div style={styles.privacy}>
              <div style={styles.privacyIc}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
              <div>
                <strong>Documents protégés.</strong> Stockés sur l'infrastructure Lexback (serveurs France, chiffrement). Jamais partagés ni utilisés pour entraîner d'IA.
              </div>
            </div>

          </div>
        </div>
      )}

      {/* === PHASE ANALYZING === */}
      {phase === 'analyzing' && (
        <div style={styles.overlay}>
          <div style={styles.overlayPanel}>
            <div style={styles.spinnerBig}>
              <div style={{
                position: 'absolute', inset: 0,
                border: '3px solid #ebe9e2', borderTopColor: '#c78c2a',
                borderRadius: '50%', animation: 'spin 1s linear infinite',
              }}/>
            </div>
            <div style={styles.overlayTitle}>Analyse en cours</div>
            <div style={styles.overlayStep}>{analyzeStep}</div>
            <div style={styles.progressTrack}>
              <div style={{ ...styles.progressBar, width: `${analyzeProgress}%` }}/>
            </div>
          </div>
        </div>
      )}

      {/* === PHASE MODAL === */}
      {phase === 'modal' && (
        <div style={styles.modalBg}>
          <div style={styles.modalCard}>
            <div style={styles.pillWarn}>⚠ Précision nécessaire</div>
            <h2 style={styles.modalTitle}>Quelques <span style={styles.heroEm}>précisions</span></h2>
            <p style={{ fontSize: 13, color: '#555550', margin: '0 0 18px' }}>
              Je n'ai pas pu déterminer le type de formalité. Aidez-moi à finaliser.
            </p>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1a1a', marginBottom: 8 }}>
                Type de formalité <span style={{ color: '#8e8b85', fontWeight: 400 }}>(à préciser)</span>
              </div>
              <div>
                {[
                  { id: 'dirigeant', label: 'Changement de dirigeant' },
                  { id: 'siege', label: 'Transfert de siège' },
                  { id: 'capital_aug', label: 'Augmentation de capital' },
                  { id: 'cession', label: 'Cession de parts' },
                  { id: 'denomination', label: 'Changement de dénomination' },
                  { id: 'autre', label: 'Autre — préciser dans le commentaire' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    style={{
                      ...styles.modalOption,
                      ...(modalAnswer === opt.id ? styles.modalOptionSelected : {}),
                    }}
                    onClick={() => setModalAnswer(opt.id)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              marginTop: 18, paddingTop: 16, borderTop: '1px solid #ece9e2',
            }}>
              <button style={styles.modalSkip} onClick={() => closeModal(true)}>
                Sauter (le formaliste décidera)
              </button>
              <div style={{ flex: 1 }}/>
              <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={() => closeModal(false)}>
                Continuer →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === PHASE CONFIRM === */}
      {phase === 'confirm' && analyzeResult && (
        <div style={styles.pane}>
          <div style={styles.inner}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={styles.confirmPill}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Analyse terminée
              </div>
              <h1 style={{ ...styles.heroTitle, fontSize: 30, marginBottom: 6 }}>
                Voici ce que <span style={styles.heroEm}>j'ai compris</span>
              </h1>
              <p style={styles.heroDesc}>
                Vérifiez le récap puis confirmez l'envoi au formaliste.
              </p>
            </div>

            {error && (
              <div style={{ ...styles.privacy, background: '#fbe9e7', borderColor: '#fda3a0', color: '#b3261e', marginBottom: 14 }}>
                <div style={{ ...styles.privacyIc, background: '#b3261e' }}>!</div>
                <div>{error}</div>
              </div>
            )}

            {/* Société */}
            <div style={styles.confirmCard}>
              <div style={styles.confirmCardHead}>
                <div style={styles.confirmCardTitle}>Société</div>
                <button style={styles.confirmEdit} onClick={() => setPhase('form')}>Modifier</button>
              </div>
              {tab === 'creation' ? (
                <div style={{ ...styles.societeCard, borderColor: '#bfdbfe', background: '#eff6ff' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={styles.societeName}>Création de société</div>
                      <div style={styles.societeMeta}>
                        <span><span style={{ color: '#8e8b85' }}>SIREN :</span> sera attribué après immatriculation</span>
                      </div>
                    </div>
                    <span style={styles.pillBlue}>Nouveau</span>
                  </div>
                </div>
              ) : (
                societe && <SocieteCard societe={societe} siren={siren} draftId={draftId} />
              )}
            </div>

            {/* Formalité (éditable inline) */}
            <div style={styles.confirmCard}>
              <div style={styles.confirmCardHead}>
                <div style={styles.confirmCardTitle}>Formalité</div>
                <div style={{ fontSize: 11, color: '#8e8b85' }}>Modifiable ci-dessous</div>
              </div>

              {/* Chips type principal */}
              <div style={styles.typeChips}>
                {MAIN_TYPES.map((t) => (
                  <button
                    key={t.id}
                    style={{ ...styles.typeChip, ...(mainType === t.id ? styles.typeChipActive : {}) }}
                    onClick={() => {
                      if (mainType === t.id) {
                        setMainType(null);
                        setSubTypesSelected([]);
                      } else {
                        setMainType(t.id);
                        if (t.id !== 'modification') setSubTypesSelected([]);
                      }
                    }}
                  >
                    <span style={styles.typeChipIcon}>{t.icon}</span>
                    <span style={styles.typeChipLabel}>{t.label}</span>
                  </button>
                ))}
              </div>

              {/* Sous-types si modification */}
              {mainType === 'modification' && (
                <>
                  <div style={{ ...styles.blockTitle, marginTop: 16, marginBottom: 8 }}>
                    Nature de la modification
                  </div>
                  <div style={styles.subTypesGrid}>
                    {SUB_TYPES_LIST.map((st) => {
                      const selected = subTypesSelected.includes(st);
                      return (
                        <button
                          key={st}
                          style={{ ...styles.subChip, ...(selected ? styles.subChipActive : {}) }}
                          onClick={() => {
                            if (selected) {
                              setSubTypesSelected(subTypesSelected.filter((x) => x !== st));
                            } else {
                              setSubTypesSelected([...subTypesSelected, st]);
                            }
                          }}
                        >
                          {SUB_TYPE_LABELS[st] || st}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Hint de ce que l'IA avait détecté (informatif) */}
              {analyzeResult.type_formalite && (
                <div style={{
                  marginTop: 14, padding: '10px 12px',
                  background: '#f7efd9', borderRadius: 8,
                  fontSize: 12, color: '#6b4f00',
                }}>
                  <strong>L'IA a détecté :</strong> {TYPE_LABELS[analyzeResult.type_formalite] || analyzeResult.type_formalite}
                  {analyzeResult.sous_types?.length > 0 && (
                    <> · {analyzeResult.sous_types.map((s) => SUB_TYPE_LABELS[s] || s).join(', ')}</>
                  )}
                </div>
              )}
            </div>

            {/* Pièces */}
            <div style={styles.confirmCard}>
              <div style={styles.confirmCardHead}>
                <div style={styles.confirmCardTitle}>
                  Pièces classées · <span style={{ color: '#8e8b85', fontWeight: 500 }}>{files.length} fournie{files.length > 1 ? 's' : ''}</span>
                </div>
                <button style={styles.confirmEdit} onClick={() => setPhase('form')}>Modifier</button>
              </div>
              <div>
                {files.map((f) => {
                  const ext = (f.name.split('.').pop() || '').toUpperCase();
                  const isImg = ['JPG', 'JPEG', 'PNG'].includes(ext);
                  return (
                    <div key={f.id} style={styles.docClassified}>
                      <div style={{ ...styles.fileIc, background: isImg ? '#16653a' : '#b3261e' }}>{ext}</div>
                      <div style={{ flex: 1 }}>
                        <div style={styles.fileName}>{f.name}</div>
                        <div style={{ fontSize: 11.5, color: '#8e8b85', marginTop: 1 }}>
                          {f.detected_label && f.detected_label !== 'Document non identifié' ? (
                            <>Détecté : <span style={styles.docTag}>{f.detected_label}</span></>
                          ) : (
                            <span style={{ color: '#8e8b85', fontStyle: 'italic' }}>Document non identifié — à classer par le formaliste</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Missing */}
              {analyzeResult.missing_required && analyzeResult.missing_required.length > 0 && (
                <div style={styles.missingWarn}>
                  <div style={{
                    width: 18, height: 18, background: '#b45309', color: '#fff',
                    borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>!</div>
                  <div style={{ fontSize: 12.5, color: '#78350f', lineHeight: 1.5, flex: 1 }}>
                    <strong>Pièces obligatoires manquantes :</strong>
                    <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                      {analyzeResult.missing_required.map((m) => <li key={m}>{m}</li>)}
                    </ul>
                    <div style={{ marginTop: 8, fontSize: 12 }}>
                      Vous pouvez envoyer maintenant et compléter plus tard.
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Commentaire */}
            <div style={styles.confirmCard}>
              <div style={styles.confirmCardHead}>
                <div style={styles.confirmCardTitle}>Votre commentaire</div>
                <button style={styles.confirmEdit} onClick={() => setPhase('form')}>Modifier</button>
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {comment || <em style={{ color: '#8e8b85' }}>Aucun commentaire ajouté.</em>}
              </div>
            </div>

            {/* Provision */}
            <div style={styles.confirmCard}>
              <div style={styles.confirmCardHead}>
                <div style={styles.confirmCardTitle}>Provision à prévoir</div>
              </div>
              <div style={styles.provisionRow}>
                <div style={{ flex: 1 }}>
                  <div style={styles.confirmCardTitle}>Frais officiels</div>
                  <div style={{ fontSize: 11.5, color: '#8e8b85', marginTop: 3 }}>
                    {getFees() && getFees().total > 0 ? (
                      <>Greffe {getFees().greffe.toFixed(2)} €{getFees().jal > 0 ? ` · JAL ${getFees().jal.toFixed(2)} €` : ''}</>
                    ) : (
                      <>À déterminer après précisions</>
                    )}
                  </div>
                  <div style={styles.walletWarn}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    Pensez à recharger votre Wallet si ce n'est pas déjà fait.
                  </div>
                </div>
                <div style={styles.provisionAmount}>
                  {getFees() && getFees().total > 0 ? `${getFees().total.toFixed(2)} €` : '— €'}
                  <span style={styles.provisionIndic}>à titre indicatif</span>
                </div>
              </div>
              <div style={{ fontSize: 11.5, color: '#8e8b85', fontStyle: 'italic', marginTop: 10 }}>
                À payer avant envoi du dossier au greffe du tribunal de commerce. Les honoraires Lexback sont inclus dans votre forfait.
              </div>
            </div>

            <div style={styles.confirmActions}>
              <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={() => setPhase('form')}>
                Retour
              </button>
              <button
                style={{ ...styles.btn, ...styles.btnPrimary }}
                onClick={handleSubmit}
                disabled={phase === 'submitting'}
              >
                {phase === 'submitting' ? 'Envoi…' : 'Confirmer & envoyer →'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === PHASE SENT === */}
      {phase === 'sent' && (
        <div style={{ ...styles.pane, paddingTop: 80, textAlign: 'center' }}>
          <div style={{ maxWidth: 540, margin: '0 auto' }}>
            <div style={styles.sentIcon}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <h1 style={{ ...styles.heroTitle, fontSize: 30 }}>Dossier envoyé au formaliste</h1>
            <p style={styles.heroDesc}>
              Référence <strong>{finalReference}</strong> · Le formaliste a été notifié et reviendra vers vous sous 24h ouvrées.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginTop: 24 }}>
              <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={resetAll}>
                Créer un autre dossier
              </button>
              <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={() => navigate('/dossiers')}>
                Voir mes dossiers
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// =============================================================================
// COMPOSANT — Carte société avec téléchargements Pappers
// =============================================================================

function SocieteCard({ societe, siren, draftId }) {
  const downloadPappers = async (kind) => {
    if (!siren) return;
    try {
      const res = await api.get(`/api/dossiers/pappers/${siren}/${kind}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${kind}_${siren}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      console.error('Pappers download failed:', err);
      alert(`Téléchargement ${kind} indisponible pour ce SIREN`);
    }
  };

  return (
    <div style={styles.societeCard}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={styles.societeName}>{societe.nom}</div>
          <div style={styles.societeMeta}>
            {siren && <span><span style={{ color: '#8e8b85' }}>SIREN :</span> {siren}</span>}
            {societe.forme && <span><span style={{ color: '#8e8b85' }}>Forme :</span> {societe.forme}</span>}
            {societe.capital && <span><span style={{ color: '#8e8b85' }}>Capital :</span> {societe.capital}</span>}
          </div>
          {societe.adresse && (
            <div style={{ ...styles.societeMeta, marginTop: 4 }}>
              <span><span style={{ color: '#8e8b85' }}>Siège :</span> {societe.adresse}</span>
            </div>
          )}
        </div>
        <span style={styles.pillGreen}>{societe.statut || 'Actif'}</span>
      </div>
      <div style={styles.societeActions}>
        <button style={styles.societeActionBtn} onClick={() => downloadPappers('extrait')}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
          Statuts
        </button>
        <button style={styles.societeActionBtn} onClick={() => downloadPappers('extrait')}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
          Extrait Pappers
        </button>
        <button style={styles.societeActionBtn} onClick={() => downloadPappers('avis_insee')}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
          Avis INSEE
        </button>
      </div>
    </div>
  );
}
